import React, { useEffect, useState } from "react";
import { FileText, Download } from "lucide-react";
import Topbar from "@/components/layout/Topbar";
import DateRangePicker, { rangeToQueryString } from "@/components/DateRangePicker";
import { api, API, formatMoneyFull, getToken } from "@/lib/api";
import { toast, Toaster } from "sonner";

export default function VCReporting() {
  const [range, setRange] = useState({ months: 12 });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    setLoading(true);
    const qs = rangeToQueryString(range);
    api
      .get(`/vc/report?${qs}`)
      .then((r) => setReport(r.data))
      .catch(() => toast.error("Failed to load VC report"))
      .finally(() => setLoading(false));
  }, [range]);

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      const qs = rangeToQueryString(range);
      const res = await fetch(`${API}/vc/export/${fmt}?${qs}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `honey_badger_vc_report.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDownloading(null);
    }
  };

  const Section = ({ title, rows }) => (
    <div className="rounded-md border border-slate-200 bg-white">
      <div className="border-b border-slate-100 bg-navy-600 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
        {title}
      </div>
      <table className="w-full text-sm">
        <tbody>
          {(rows || []).map((r, i) => (
            <tr
              key={r.label + i}
              className={[
                "border-b border-slate-100 last:border-0",
                i === (rows?.length ?? 0) - 1 ? "bg-slate-50 font-semibold" : "",
              ].join(" ")}
            >
              <td className="px-5 py-2.5 text-slate-700">{r.label}</td>
              <td className="px-5 py-2.5 text-right tabular text-navy-600">
                {typeof r.value === "number"
                  ? r.label.includes("%")
                    ? `${r.value.toFixed(1)}%`
                    : formatMoneyFull(r.value)
                  : r.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title="VC Financial Health Report"
        subtitle="Standardized extraction · ready for funding rounds"
        right={
          <div className="flex items-center gap-2">
            <DateRangePicker value={range} onChange={setRange} testId="vc-range" />
            {["pdf", "xlsx", "csv"].map((fmt) => (
              <button
                key={fmt}
                type="button"
                onClick={() => download(fmt)}
                disabled={downloading === fmt}
                data-testid={`vc-export-${fmt}`}
                className={`flex h-9 items-center gap-1.5 rounded-sm px-3 text-xs font-semibold uppercase tracking-widest transition-colors ${
                  fmt === "pdf"
                    ? "bg-brand-orange text-white hover:bg-[#C1701C]"
                    : "border border-slate-200 text-slate-600 hover:border-navy-600 hover:text-navy-600"
                } disabled:opacity-50`}
              >
                <Download className="h-3.5 w-3.5" />
                {downloading === fmt ? "…" : fmt}
              </button>
            ))}
          </div>
        }
      />

      <div className="space-y-6 p-6 animate-fade-in" data-testid="vc-reporting">
        {loading ? (
          <div className="text-sm text-slate-500">Generating report…</div>
        ) : report ? (
          <>
            <div className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-navy-600 text-white">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-orange">
                    Confidential — VC Disclosure
                  </div>
                  <h2 className="font-display text-2xl font-semibold tracking-tight text-navy-600">
                    Project Honey Badger
                  </h2>
                  <div className="text-xs text-slate-500">
                    Period: {report.period_label}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <Section title="Income Statement" rows={report.income_statement} />
              <Section title="Balance Sheet (est.)" rows={report.balance_sheet} />
              <Section title="Cash Flow (est.)" rows={report.cash_flow} />
              <Section title="Key Metrics" rows={report.key_metrics} />
            </div>

            <Section
              title="Budget vs Spend by Category"
              rows={report.budget_vs_spend}
            />
          </>
        ) : (
          <div className="text-sm text-slate-500">No data.</div>
        )}
      </div>
    </>
  );
}
